import { X } from "@tamagui/lucide-icons";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { ScrollView, XStack, YStack } from "tamagui";

import {
  Button,
  ConstrainedView,
  Footer,
  Logo,
  RadioGroup,
  Spacer,
} from "@/components";
import { Heading, Paragraph, Text } from "@/components/Typography";
import type { QuestionnaireDefinition } from "./QuestionnaireTypes";

export type QuestionnaireAnswers = Record<string, Record<string, string>>;

export type QuestionnaireRendererProps = {
  definitions: QuestionnaireDefinition[];
  onClose: () => void;
  initialAnswers?: QuestionnaireAnswers;
  onAnswersChange?: (answers: QuestionnaireAnswers) => void;
  onSubmit?: (
    answers: QuestionnaireAnswers,
  ) => boolean | void | Promise<boolean | void>;
  onComplete?: () => boolean | void | Promise<boolean | void>;
  completionButtonLabel?: string;
  showCompletionMessage?: boolean;
  showHeader?: boolean;
  showDefinitionTitle?: boolean;
  onBackButtonChange?: (show: boolean, onPress?: (() => void) | null) => void;
  renderCompletionContent?: (
    answers: QuestionnaireAnswers,
  ) => ReactNode | undefined;
};

type ScrollTarget = {
  scrollIntoView?: (options?: { behavior?: "smooth"; block?: "start" }) => void;
};

const createInitialAnswers = (
  definitions: QuestionnaireDefinition[],
  initialAnswers: QuestionnaireAnswers = {},
) =>
  definitions.reduce<QuestionnaireAnswers>((answers, definition) => {
    answers[definition.id] = initialAnswers[definition.id] ?? {};
    return answers;
  }, {});

const getButtonLabel = ({
  activeIndex,
  definitionCount,
  isComplete,
  completionButtonLabel,
  missingQuestionId,
}: {
  activeIndex: number;
  definitionCount: number;
  isComplete: boolean;
  completionButtonLabel: string;
  missingQuestionId: string | null;
}) => {
  if (isComplete) return completionButtonLabel;
  if (activeIndex < definitionCount - 1) return "Next questionnaire";
  return missingQuestionId ? "Continue" : "Complete questionnaire";
};

const getCompletionContent = (
  renderCompletionContent: QuestionnaireRendererProps["renderCompletionContent"],
  answers: QuestionnaireAnswers,
  defaultContent: ReactNode,
): ReactNode => renderCompletionContent?.(answers) ?? defaultContent;

const QuestionnaireStepIndicator = ({
  activeIndex,
  definitionCount,
  isComplete,
}: {
  activeIndex: number;
  definitionCount: number;
  isComplete: boolean;
}) => {
  if (isComplete) return null;

  return (
    <XStack justifyContent="space-between" alignItems="center">
      <Heading size="$md">Questionnaire</Heading>
      <Text size="$sm" color="$textSecondary">
        {activeIndex + 1} of {definitionCount}
      </Text>
    </XStack>
  );
};

export default function QuestionnaireRenderer({
  definitions,
  onClose,
  initialAnswers,
  onAnswersChange,
  onSubmit,
  onComplete,
  completionButtonLabel = "Close",
  showCompletionMessage = false,
  showHeader = true,
  showDefinitionTitle = true,
  onBackButtonChange,
  renderCompletionContent,
}: QuestionnaireRendererProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [answers, setAnswers] = useState(() =>
    createInitialAnswers(definitions, initialAnswers),
  );
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const scrollViewRef = useRef<any>(null);
  const questionRefs = useRef<Record<string, ScrollTarget | null>>({});
  const isFirstAnswersRender = useRef(true);

  const activeDefinition = definitions[activeIndex];
  const activeAnswers = activeDefinition
    ? (answers[activeDefinition.id] ?? {})
    : {};
  const requiredQuestions =
    activeDefinition?.questions.filter(({ required }) => required) ?? [];
  const missingQuestionId = useMemo(
    () => requiredQuestions.find(({ id }) => !activeAnswers[id])?.id ?? null,
    [activeAnswers, requiredQuestions],
  );

  useEffect(() => {
    scrollViewRef.current?.scrollTo?.({ y: 0, animated: activeIndex > 0 });
  }, [activeIndex]);

  useEffect(() => {
    if (isFirstAnswersRender.current) {
      isFirstAnswersRender.current = false;
      return;
    }
    onAnswersChange?.(answers);
  }, [answers, onAnswersChange]);

  useEffect(() => {
    onBackButtonChange?.(false, isComplete ? null : undefined);
  }, [isComplete, onBackButtonChange]);

  if (!activeDefinition) return null;

  const selectAnswer = (questionId: string, value: string) => {
    setHasSubmitted(false);
    setAnswers((currentAnswers) => {
      const nextAnswers = {
        ...currentAnswers,
        [activeDefinition.id]: {
          ...currentAnswers[activeDefinition.id],
          [questionId]: value,
        },
      };
      return nextAnswers;
    });
  };

  const scrollToFirstError = () => {
    if (!missingQuestionId) return;

    const target = questionRefs.current[missingQuestionId];
    if (target?.scrollIntoView) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }

    scrollViewRef.current?.scrollTo?.({ y: 0, animated: true });
  };

  const handleContinue = async () => {
    if (missingQuestionId) {
      setHasSubmitted(true);
      requestAnimationFrame(scrollToFirstError);
      return;
    }

    if (activeIndex < definitions.length - 1) {
      setActiveIndex((index) => index + 1);
      setHasSubmitted(false);
      return;
    }

    setIsSubmitting(true);
    try {
      const advanced = await onSubmit?.(answers);
      if (advanced !== false) setIsComplete(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleComplete = async () => {
    setIsSubmitting(true);
    try {
      if (onComplete) await onComplete();
      else onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const buttonLabel = getButtonLabel({
    activeIndex,
    definitionCount: definitions.length,
    isComplete,
    completionButtonLabel,
    missingQuestionId,
  });

  return (
    <ConstrainedView flex={1} paddingVertical="$300" gap="$300">
      {showHeader && (
        <XStack justifyContent="space-between" alignItems="center">
          <Spacer size="$200" />
          <Logo />
          <Button.Wrapper
            round
            onPress={onClose}
            aria-label="Close questionnaire"
            dataTrackingId="questionnaire-close"
            padding="$100"
            backgroundColor="$transparent"
          >
            <X size={20} />
          </Button.Wrapper>
        </XStack>
      )}

      <QuestionnaireStepIndicator
        activeIndex={activeIndex}
        definitionCount={definitions.length}
        isComplete={isComplete}
      />

      {isComplete ? (
        getCompletionContent(
          renderCompletionContent,
          answers,
          <YStack
            flex={1}
            justifyContent="center"
            alignItems="center"
            gap="$200"
          >
            <Heading size="$lg" textAlign="center">
              Thanks for completing the questionnaires
            </Heading>
            {showCompletionMessage && activeDefinition.completionMessage && (
              <Paragraph textAlign="center">
                {activeDefinition.completionMessage}
              </Paragraph>
            )}
          </YStack>,
        )
      ) : (
        <ScrollView ref={scrollViewRef} flex={1}>
          <YStack gap="$300" paddingBottom="$400">
            <YStack gap="$200">
              {showDefinitionTitle && (
                <Heading size="$md">{activeDefinition.title}</Heading>
              )}
              {activeDefinition.description && (
                <Paragraph color="$textSecondary">
                  {activeDefinition.description}
                </Paragraph>
              )}
            </YStack>

            {activeDefinition.questions.map((question, questionIndex) => {
              const selected = activeAnswers[question.id];
              const hasError = hasSubmitted && question.required && !selected;

              return (
                <YStack
                  key={`${activeDefinition.id}-${question.id}`}
                  ref={(node) => {
                    questionRefs.current[question.id] = node as ScrollTarget;
                  }}
                  gap="$150"
                >
                  <YStack gap="$50">
                    <Text size="$xs" color="$textSecondary">
                      Question {questionIndex + 1}
                    </Text>
                    <Heading size="$sm">{question.text}</Heading>
                  </YStack>
                  <RadioGroup
                    borderless
                    value={selected}
                    onValueChange={(value: string) =>
                      selectAnswer(question.id, value)
                    }
                    errorText={hasError ? "Please select an answer" : undefined}
                    dataTrackingId={`questionnaire-${activeDefinition.id}-${question.id}`}
                    width="100%"
                  >
                    {question.options.map((option) => (
                      <RadioGroup.Item
                        key={option.value}
                        id={`${activeDefinition.id}-${question.id}-${option.value}`}
                        value={option.value}
                        width="100%"
                      >
                        {option.label}
                      </RadioGroup.Item>
                    ))}
                  </RadioGroup>
                </YStack>
              );
            })}
          </YStack>
        </ScrollView>
      )}

      <Footer mode="dialog">
        <Button
          mode="primary"
          constrained
          onPress={isComplete ? handleComplete : handleContinue}
          disabled={isSubmitting}
          loading={isSubmitting}
          dataTrackingId={
            isComplete
              ? onComplete
                ? "questionnaire-complete-continue"
                : "questionnaire-close-complete"
              : "questionnaire-continue"
          }
        >
          {buttonLabel}
        </Button>
      </Footer>
    </ConstrainedView>
  );
}
